import { Col, Descriptions, Row, Space, Table, Typography, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import { useContext, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';
import { CiLocationOn } from 'react-icons/ci';
import { GrMapLocation } from 'react-icons/gr';
import { IoWarningOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { useHistory } from 'react-router-dom';

import ProductImage from '../../../components/productImage';
import { clearObjFields } from '../../../utils';
import { isHandledByInterceptor } from '../../../utils/apiError';
import { formatCurrency } from '../../../utils/format';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import { clearDraft } from './draft';

const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Тоог нийлбэрт нэмэхэд NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const columns = [
  {
    key: 'index',
    title: '№',
    width: 48,
    align: 'center',
    render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
  },
  {
    key: 'product',
    title: 'Бараа',
    render: (_, record) => (
      <Space size={12} align="center">
        <ProductImage
          src={record?.imageCover}
          alt={record?.title ?? 'Барааны зураг'}
          size={IMAGE_SIZE}
          style={{
            objectFit: 'contain',
            borderRadius: 4,
            border: '1px solid #eaecf0',
            boxShadow: IMAGE_SHADOW,
          }}
        />
        <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
          <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
            {val(record?.title)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {record?.barCodes?.[0] ?? record?.productSku}
            {record?.expiryDate ? ` · Ex: ${moment(record?.expiryDate).format('YYYY-MM-DD')}` : ''}
          </Text>
        </Space>
      </Space>
    ),
  },
  {
    key: 'price',
    title: 'Нэгжийн үнэ',
    align: 'center',
    width: 140,
    render: (_, record) => formatCurrency(num(record?.price)),
  },
  {
    key: 'availableStock',
    title: 'Үлдэгдэл',
    align: 'center',
    width: 120,
    render: (_, record) => (record?.availableStock != null ? `${record.availableStock} ш` : '-'),
  },
  {
    key: 'totalUnit',
    title: 'Тоо ширхэг',
    align: 'center',
    width: 120,
    render: (_, record) => `${num(record?.totalUnit)} ш`,
  },
  {
    key: 'total',
    title: 'Нийт дүн',
    align: 'center',
    width: 160,
    render: (_, record) => (
      <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
    ),
  },
];

/** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт дүн */
const renderSummary = (rows = []) => {
  const totalUnit = rows.reduce((total, record) => total + num(record?.totalUnit), 0);
  const totalPrice = rows.reduce(
    (total, record) => total + num(record?.totalUnit) * num(record?.price),
    0
  );

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={4} align="right">
        <Text type="secondary">Нийт</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} align="center">
        <Text strong>{`${totalUnit} ш`}</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="center">
        <Text strong>{formatCurrency(totalPrice)}</Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

const Step3 = ({ prevStep }) => {
  const history = useHistory();
  const addContext = useContext(AddOrderDeliveryContext);
  const [submitting, setSubmitting] = useState(false);

  const formData = { ...addContext.formDataStep1, ...addContext.formDataStep2 };
  const isExpress = formData?.deliveryTime === 'express';

  const onCreate = async () => {
    const reqData = {
      ...addContext.formDataStep1,
      deliveryType: formData.deliveryType || addContext?.selectedDeliveryType,
      addressDetail: addContext?.addressDetail,
      isExpress,
      deliveryDetail: formData.deliveryDetail,
      deliveryPositionCode: formData?.deliveryPositionCode,
      pricingCode: formData?.pricingCode,
      dropoffDate: formData?.dropoffDate ?? '',
      dropoffTime: formData?.dropoffTime ?? [],
      notes: formData?.notes ?? [],
      orderDescription: formData?.orderDescription,
      deliveryPhone: formData?.phoneNumber,
      deliveryEmail: formData?.email,
      deliveryName: formData?.firstName,
      serviceTotalAmount: formData?.totalPrice,
      attachment: formData?.attachment,
      dropoffAddressGeo: formData.position,
      warehouseCode: formData?.warehouseCode,
      solvedPrice: formData?.solvedPrice,
      integrationWarehouseId: formData?.ingtegration_Warehouse,
      outOfZoneDistanceKm: formData?.outOfZoneDistanceKm,
    };

    if (Array.isArray(formData.addressArray) && formData.addressArray.length > 0) {
      reqData.deliveryCode = formData.addressArray.map((e, i) => {
        const { _id, ...addressData } = e || {};
        return { ...addressData, order: 100 * (i + 1) };
      });
    }

    if (formData?.customerId) reqData.customerId = formData.customerId;
    if (formData?.customerUpdate) reqData.customerUpdate = formData.customerUpdate;

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        '/api/v1/request/create-delivery-request',
        clearObjFields(reqData)
      );

      if (data.statusCode === 200) {
        message.success('Захиалга амжилттай үүслээ.');
        // Дараагийн захиалгад хагас дутуу мэдээлэл нь сэргэхгүй байхын тулд
        clearDraft(addContext?.draftKey);
        history.push('/merchant/order/delivery');
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        message.error(data?.message_mn ?? data?.message ?? 'Захиалга үүсгэхэд алдаа гарлаа.', 6);
      }
    } catch (e) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Create delivery order error: ', e);
    } finally {
      setSubmitting(false);
    }
  };

  /** Агуулах/салбар тус бүрийн бараанууд — картанд хүснэгттэйгээ */
  const renderProductGroups = (groups = [], { title, icon, detailSource }) =>
    (groups ?? [])
      .filter((group) => group?.products?.length)
      .map((group) => {
        const code = group.products[0]?.productLocationCode;
        const detail = detailSource?.[code];

        return (
          <SectionCard key={group.id} icon={icon} title={title} styles={{ body: { padding: 0 } }}>
            <Descriptions
              {...descriptionProps}
              style={{ padding: '12px 16px 0' }}
              items={[
                { key: 'name', label: 'Нэр', children: val(detail?.name) },
                { key: 'address', label: 'Дэлгэрэнгүй хаяг', children: val(detail?.addressName) },
              ]}
            />
            <div style={{ padding: '10px' }}>
              <Table
                columns={columns}
                dataSource={group.products}
                rowKey={(record, index) => record?._id ?? index}
                size="middle"
                pagination={false}
                summary={renderSummary}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </SectionCard>
        );
      });

  /**
   * Бүсээс хэтэрсэн нэмэлт төлбөр (Step2 дээр front талдаа бодогдсон).
   * Серверийн `totalAmount`-д ороогүй тул харуулах нийт дүн дээр нэмнэ.
   */
  const outOfZoneFee = num(formData?.outOfZoneFee);
  const totalPrice = num(formData?.totalPrice) + outOfZoneFee;

  const paymentItems = [
    {
      key: 'delivery',
      label: 'Хүргэлтийн төлбөр',
      children: formatCurrency(num(formData?.deliveryPrice)),
    },
    ...(outOfZoneFee
      ? [
          {
            key: 'outOfZone',
            label: formData?.outOfZoneDistanceKm
              ? `Бүсээс хэтэрсэн нэмэлт төлбөр (${formData.outOfZoneDistanceKm} км)`
              : 'Бүсээс хэтэрсэн нэмэлт төлбөр',
            children: formatCurrency(outOfZoneFee),
          },
        ]
      : []),
    {
      key: 'execution',
      label: 'Захиалга гүйцэтгэлийн үнийн дүн',
      children: formatCurrency(num(formData?.executionPrice)),
    },
    ...(isExpress
      ? [
          {
            key: 'express',
            label: 'Шуурхай хүргэлтийн төлбөр',
            children: formatCurrency(num(formData?.expressAmount)),
          },
          {
            key: 'exceeded',
            label: '2км-ээс хэтэрсэн нэмэлт төлбөр',
            children: formatCurrency(num(formData?.exceededAmount)),
          },
        ]
      : []),
    {
      key: 'total',
      label: 'Нийт төлбөр (НӨАТ-гүй)',
      children: <Text strong>{formatCurrency(totalPrice)}</Text>,
    },
  ];

  const dropoffTime =
    formData.dropoffTime?.length > 1
      ? `${moment(formData.dropoffTime[0]).format('HH:mm')} - ${moment(
          formData.dropoffTime[1]
        ).format('HH:mm')}`
      : '-';

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <SectionCard icon={<RiMoneyDollarCircleLine />} title="Төлбөрийн мэдээлэл">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, lg: paymentItems.length }}
          items={paymentItems}
        />
      </SectionCard>

      {renderProductGroups(addContext.warehouseList, {
        title: 'Бараа татах агуулах',
        icon: <BsBox2 />,
        detailSource: addContext.reference?.warehouse?.detail,
      })}

      {renderProductGroups(addContext.branchList, {
        title: 'Бараа татах салбар',
        icon: <BsBox2 />,
        detailSource: addContext.reference?.branches?.detail,
      })}

      <SectionCard icon={<AiOutlineInfoCircle />} title="Харилцагчийн мэдээлэл">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, lg: 4 }}
          items={[
            { key: 'name', label: 'Нэр', children: val(formData?.firstName) },
            { key: 'phone', label: 'Утас', children: val(formData?.phoneNumber) },
            { key: 'email', label: 'И-мэйл', children: val(formData?.email) },
            {
              key: 'warehouse',
              label: 'Агуулахын код',
              children: val(formData?.ingtegration_Warehouse),
            },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<CiLocationOn />} title="Хүлээж авах хаяг">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, lg: 4 }}
          items={[
            { key: 'city', label: 'Хот/аймаг', children: val(formData?.addressArray?.[0]?.name) },
            {
              key: 'district',
              label: 'Дүүрэг/сум',
              children: val(formData?.addressArray?.[1]?.name),
            },
            { key: 'khoroo', label: 'Хороо/баг', children: val(formData?.addressArray?.[2]?.name) },
            { key: 'detail', label: 'Дэлгэрэнгүй хаяг', children: val(formData?.deliveryDetail) },
          ]}
        />
      </SectionCard>

      {/* Хоёулаа 2 талбартай тул нэг мөрөнд хажуу хажуугаар (жижиг дэлгэц дээр доош бөхнө) */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SectionCard icon={<GrMapLocation />} title="Хүргэлтийн төрөл" style={{ height: '100%' }}>
            <Descriptions
              {...descriptionProps}
              items={[
                {
                  key: 'from',
                  label: 'Хаанаас',
                  children: formData?.deliveryPositionCode
                    ? `${formData.deliveryPositionCode}, ${val(formData.sourceAddress)}`
                    : '-',
                },
                {
                  key: 'type',
                  label: 'Авах хэлбэр',
                  children: val(
                    addContext.reference?.deliveryType?.detail?.[formData?.deliveryType]
                  ),
                },
              ]}
            />
          </SectionCard>
        </Col>
        <Col xs={24} lg={12}>
          <SectionCard icon={<MdDateRange />} title="Хүлээж авах огноо" style={{ height: '100%' }}>
            <Descriptions
              {...descriptionProps}
              items={[
                {
                  key: 'date',
                  label: 'Авах огноо',
                  children: formData.dropoffDate
                    ? dayjs(formData.dropoffDate).format('YYYY/MM/DD')
                    : '-',
                },
                { key: 'time', label: 'Авах цаг', children: dropoffTime },
              ]}
            />
          </SectionCard>
        </Col>
      </Row>

      <SectionCard icon={<IoWarningOutline />} title="Анхаарах шаардлагатай зүйлс">
        <Descriptions
          {...descriptionProps}
          column={2}
          items={[
            {
              key: 'notes',
              label: 'Анхаарах шаардлагатай зүйлс',
              children:
                Array.isArray(formData?.notes) && formData.notes.length
                  ? formData.notes.join(', ')
                  : '-',
            },
            {
              key: 'description',
              label: 'Тэмдэглэл',
              children: (
                <div style={{ overflowWrap: 'break-word' }}>{val(formData?.orderDescription)}</div>
              ),
            },
          ]}
        />
      </SectionCard>

      <StepFooter
        onPrev={prevStep}
        onSubmit={onCreate}
        htmlType="button"
        submitText="Захиалга үүсгэх"
        loading={submitting}
        extra={
          <Text type="secondary">
            Нийт төлбөр: <Text strong>{formatCurrency(totalPrice)}</Text>
          </Text>
        }
      />
    </Space>
  );
};

export default Step3;
